import React, { useState } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import Button from 'flow/components/ui-components/Button';
import AnchoredPopover from 'flow/components/meeting/shared/AnchoredPopover';
import { MessageContent } from './MessageInput';
import { twMerge } from 'tailwind-merge';
import useEmojiLanguage from 'flow/hooks/useEmojiLanguage';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    messageContent: MessageContent;
    textAreaRef: React.RefObject<HTMLTextAreaElement>;
    updateMessage: (update: string, cursorPosition: number) => void;
    onEmojiButtonClick?: () => void;
    onEmojiAdded?: () => void;
    className?: string;
    disabled?: boolean;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const EmojiPicker = ({
    textAreaRef,
    messageContent,
    updateMessage,
    onEmojiButtonClick,
    onEmojiAdded,
    className = '',
    disabled,
    videoDark = false,
}: Props) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const { emojiPickerI18n } = useEmojiLanguage();
    const { i18next } = useAppContext();

    const hidePicker = () => setShowEmojiPicker(false);

    return (
        <div className="relative" ref={setAnchorEl}>
            <Button
                iconKey="SentimentSatisfiedAlt"
                aria-label={i18next.t('Chat.Tooltip.Emoji')}
                aria-expanded={showEmojiPicker}
                className={twMerge(
                    'size-8',
                    videoDark
                        ? 'text-gray-300 hover:bg-white/10'
                        : 'text-gray-700 hover:bg-black/10',
                )}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowEmojiPicker((prev) => !prev);

                    if (!showEmojiPicker && onEmojiButtonClick) {
                        onEmojiButtonClick();
                    }
                }}
                disabled={disabled}
            />

            {/* The popover is portaled out of the composer, but React still
                bubbles its clicks to this parent, and some composers focus
                their text field on any click within them. Without stopping the
                click here the picker's own search field is focused by the
                browser and then immediately blurred again, leaving the user
                unable to type a search term. */}
            {showEmojiPicker && (
                <AnchoredPopover
                    anchorEl={anchorEl}
                    onClose={hidePicker}
                    placement="top-end"
                    className={twMerge('w-[320px]', className)}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Picker
                        i18n={emojiPickerI18n}
                        data={data}
                        perLine={8}
                        theme={
                            videoDark ||
                            localStorage.getItem('theme') === 'dark'
                                ? 'dark'
                                : 'light'
                        }
                        onEmojiSelect={(e: { native: string }) => {
                            // Get the current text and cursor position
                            const cursorPositionStart =
                                textAreaRef.current.selectionStart;
                            const cursorPositionEnd =
                                textAreaRef.current.selectionEnd;
                            const textBeforeCursor =
                                messageContent.text.substring(
                                    0,
                                    cursorPositionStart,
                                );
                            const textAfterCursor =
                                messageContent.text.substring(
                                    cursorPositionEnd,
                                );

                            // Construct the new message content with the emoji inserted
                            const newText =
                                textBeforeCursor + e.native + textAfterCursor;

                            const newCursorPosition =
                                cursorPositionStart + e.native.length;

                            textAreaRef.current.focus();

                            updateMessage(newText, newCursorPosition);

                            if (onEmojiAdded) {
                                onEmojiAdded();
                            }
                        }}
                        previewPosition="none"
                    />
                </AnchoredPopover>
            )}
        </div>
    );
};
export default React.memo(EmojiPicker);
